import {
  Button,
  Checkbox,
  Flex,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { modals } from '@mantine/modals'
import { QueryStatus } from '@reduxjs/toolkit/query'
import { IconX } from '@tabler/icons-react'
import withErrorBoundaryAndPolicyCheck from '@v2/commons/HOC/ErrorBoundaryAndPolicyCheckHOC'
import PolicyCheckHOC from '@v2/commons/HOC/policyCheckHOC'
import ModalSelect from '@v2/commons/components/ModalSelect/modalSelect.component'
import { POLICIES } from '@v2/commons/config'
import { useRouter } from '@v2/commons/utils/navigation'
import useUpdateEscalation from '@v2/domains/escalation/hooks/useUpdateEscalation.hook'
import { TASK_PRIORITY_OPTIONS } from '@v2/domains/task/task.types'
import useGetUsersNoPaginationUseCase from '@v2/domains/user/hooks/useGetUsersNoPagination.useCase'
import { useEffect, useState } from 'react'

import { Icons } from 'src/common/components/ui'

import {
  ESCALATION_ISSUE_OPTIONS,
  ESCALATION_PROGRAM_OPTIONS,
  ESCALATION_SERVICE_OPTIONS,
  ESCALATION_STATUS_OPTIONS,
  EscalationIssueEnum,
  EscalationProgramEnum,
  EscalationStatusEnum,
  UpdateFormSubmitter,
} from '../../escalation.types'
import EscalationCaseForm from '../EscalationForm/EscalationForm.component'
import EscalationNotes from '../EscalationNotes/escalationNotes.component'
import UploadEscalationAttachments from '../FileUploader/uploadEscalationAttachments.component'
import { TOOLTIPS } from './createEscalationModal.component'
import DeleteEscalationContent from './deleteEscalationModal.component'
import styles from './escalation-modal.module.scss'
import EscalationModalSkeleton from './escalationModalSkeleton.component'

export interface ModalProps {
  selectedItem: string
  drivingLicense?: string
  driverPhoneNumber?: string
}

const UpdateEscalationModal = ({
  selectedItem,
  drivingLicense,
  driverPhoneNumber,
}: ModalProps) => {
  const {
    updateEscalationForm,
    escalation: defaultValues,
    isEscalationQuerySuccess,
    onSubmitUpdate,
    updateEscalationMutationStatus,
    isSubmittedForm,
    hasUpdateEscalationPolicy,
    chatId,
    onCloseWithoutSave,
    formFielsUpdateStatus,
  } = useUpdateEscalation(selectedItem)

  const router = useRouter()
  const { assignees } = useGetUsersNoPaginationUseCase()

  const [filteredIssues, setFilteredIssues] = useState<
    typeof ESCALATION_ISSUE_OPTIONS
  >([])
  const [filteredServices, setFilteredServices] = useState<
    typeof ESCALATION_SERVICE_OPTIONS
  >([])

  const EscalationStatusOptions = isSubmittedForm
    ? ESCALATION_STATUS_OPTIONS.map((option) => {
        const optionCopy = { ...option }
        if (
          [EscalationStatusEnum.CREATED, EscalationStatusEnum.DRAFT].includes(
            optionCopy.value,
          )
        ) {
          optionCopy.disabled = true
        } else {
          optionCopy.disabled = false
        }
        return optionCopy
      })
    : ESCALATION_STATUS_OPTIONS

  const [issue, setIssue] = useState<string | null | undefined>(
    defaultValues?.issue,
  )
  const [service, setService] = useState<string | null | undefined>(
    defaultValues?.service,
  )

  updateEscalationForm.watch('program', ({ value }) => {
    onSetIssueByProgram(value!)
  })

  updateEscalationForm.watch('issue', ({ value }) => {
    setIssue(value)
    onSetServiceByIssue(value!)
  })

  updateEscalationForm.watch('service', ({ value }) => {
    setService(value)
  })

  const onSetIssueByProgram = (value: EscalationProgramEnum) => {
    const filteredIssues = ESCALATION_ISSUE_OPTIONS.filter(
      (option) => !option.program || option.program === value!,
    )
    const fallBackIssue =
      defaultValues!.program === value ? defaultValues?.issue : null
    const fallBackService =
      defaultValues?.issue === fallBackIssue ? defaultValues?.service : null
    setFilteredIssues(filteredIssues)
    setIssue(fallBackIssue)
    updateEscalationForm.setFieldValue('issue', fallBackIssue)
    setService(fallBackService)
    updateEscalationForm.setFieldValue('service', fallBackService ?? '')
  }

  const onSetServiceByIssue = (value: EscalationIssueEnum) => {
    const filteredServices = ESCALATION_SERVICE_OPTIONS.filter(
      (option) => option.issue === value!,
    )
    const fallBackService =
      defaultValues?.issue === value ? defaultValues?.service : null
    setFilteredServices(filteredServices)
    setService(fallBackService)
    updateEscalationForm.setFieldValue('service', fallBackService ?? '')
  }

  const isIssueSelected = !!issue && filteredServices.length > 0

  useEffect(() => {
    if (isEscalationQuerySuccess) {
      onSetIssueByProgram(defaultValues?.program!)
      onSetServiceByIssue(defaultValues?.issue!)
    }
  }, [defaultValues, isEscalationQuerySuccess])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const { submitter } = event.nativeEvent as SubmitEvent

    if (submitter?.id === UpdateFormSubmitter.SUBMIT) {
      updateEscalationForm.setFieldValue(
        'formStatus',
        EscalationStatusEnum.SUBMITTED,
      )
    } else if (submitter?.id === UpdateFormSubmitter.SAVE_DRAFT) {
      updateEscalationForm.setFieldValue(
        'formStatus',
        EscalationStatusEnum.DRAFT,
      )
    }
    onSubmitUpdate()
  }
  const handleDelete = () => {
    modals.open({
      centered: true,
      withCloseButton: true,
      size: 'md',
      children: (
        <DeleteEscalationContent
          onMutationSuccess={() => modals.closeAll()}
          escalationId={selectedItem}
        />
      ),
    })
  }

  return (
    <Paper className={styles['modal-body']}>
      <Stack className={styles['header-wrapper']}>
        <Flex justify="flex-start">
          {formFielsUpdateStatus === QueryStatus.pending ? (
            <Loader className={styles['exit-button']} />
          ) : (
            <Button
              variant="transparent"
              c="black"
              p={0}
              onClick={onCloseWithoutSave}
              className={styles['exit-button']}
            >
              <IconX />
            </Button>
          )}
          <PolicyCheckHOC policyName={POLICIES.isDeleteEscalationEnabled}>
            <Button
              className={styles['delete-button']}
              variant="transparent"
              leftSection={<Icons.Trash />}
              onClick={handleDelete}
            >
              Delete Escalation
            </Button>
          </PolicyCheckHOC>
        </Flex>
        <Text className={styles['modal-header']} ta="center">
          Escalstion Information
        </Text>
      </Stack>
      {isEscalationQuerySuccess && assignees.length > 0 ? (
        <form
          onSubmit={(e) => {
            handleSubmit(e)
          }}
        >
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" mb={32}>
            <TextInput
              label="Driver's Name"
              classNames={{
                input: styles['record-info-input'],
                label: styles['record-info-label'],
              }}
              readOnly
              rightSection={
                <Icons.NewLinkTab
                  onClick={() =>
                    router.push(`/leads/${defaultValues?.person.id}`)
                  }
                  style={{ cursor: 'pointer' }}
                />
              }
              value={
                defaultValues?.person.firstName +
                ' ' +
                defaultValues?.person.lastName
              }
            />
            <TextInput
              label="Driver's TLC/DL"
              value={drivingLicense}
              classNames={{
                input: styles['record-info-input'],
                label: styles['record-info-label'],
              }}
              readOnly
            />
            <TextInput
              label="Driver's Email"
              value={defaultValues?.person.email}
              classNames={{
                input: styles['record-info-input'],
                label: styles['record-info-label'],
              }}
              readOnly
            />
            <TextInput
              label="Driver's Phone Number"
              value={driverPhoneNumber}
              classNames={{
                input: styles['record-info-input'],
                label: styles['record-info-label'],
              }}
              readOnly
            />
            <ModalSelect
              label="Program"
              placeholder="Select a Program"
              options={ESCALATION_PROGRAM_OPTIONS}
              {...updateEscalationForm.getInputProps('program')}
              key={updateEscalationForm.key('program')}
              allowDeselect={false}
              readOnly={!hasUpdateEscalationPolicy || isSubmittedForm}
              showArrowIcon={hasUpdateEscalationPolicy && !isSubmittedForm}
            />
            <Tooltip
              position="bottom"
              display={
                updateEscalationForm.getValues().program ? 'none' : 'block'
              }
              label={TOOLTIPS.issue}
            >
              <Paper>
                <ModalSelect
                  label="Issue"
                  placeholder="Select an Issue"
                  value={issue}
                  {...updateEscalationForm.getInputProps('issue')}
                  options={filteredIssues}
                  key={updateEscalationForm.key('issue')}
                  showArrowIcon={hasUpdateEscalationPolicy && !isSubmittedForm}
                  readOnly={!hasUpdateEscalationPolicy || isSubmittedForm}
                  allowDeselect={false}
                />
              </Paper>
            </Tooltip>
            <Tooltip
              position="bottom"
              display={issue ? 'none' : 'block'}
              label={TOOLTIPS.service}
            >
              <Paper>
                <ModalSelect
                  label="Benefit or Service (if applicable)"
                  placeholder="Select a benefit or service"
                  {...updateEscalationForm.getInputProps('service')}
                  options={filteredServices}
                  key={updateEscalationForm.key('service')}
                  value={service}
                  showArrowIcon={
                    hasUpdateEscalationPolicy &&
                    isIssueSelected &&
                    !isSubmittedForm
                  }
                  readOnly={!hasUpdateEscalationPolicy || isSubmittedForm}
                />
              </Paper>
            </Tooltip>
            <ModalSelect
              label="Priority"
              placeholder="Select Priority"
              {...updateEscalationForm.getInputProps('priority')}
              key={updateEscalationForm.key('priority')}
              options={TASK_PRIORITY_OPTIONS}
              allowDeselect={false}
              readOnly={!hasUpdateEscalationPolicy}
              showArrowIcon={hasUpdateEscalationPolicy}
            />
            <ModalSelect
              label="Assign To"
              placeholder="Select Assignee"
              {...updateEscalationForm.getInputProps('assignedToId')}
              options={assignees}
              key={updateEscalationForm.key('assignedToId')}
              allowDeselect={false}
              searchable
              nothingFoundMessage="No users found"
              readOnly={!hasUpdateEscalationPolicy}
              showArrowIcon={hasUpdateEscalationPolicy}
            />

            <ModalSelect
              {...updateEscalationForm.getInputProps('formStatus')}
              key={updateEscalationForm.key('formStatus')}
              label="Status"
              options={EscalationStatusOptions}
              allowDeselect={false}
              readOnly={!hasUpdateEscalationPolicy}
              showArrowIcon={hasUpdateEscalationPolicy}
            />
            <Stack style={{ alignSelf: 'center' }}>
              <Checkbox
                label="Notify Assignee via Email"
                radius="3px"
                {...updateEscalationForm.getInputProps('isNotify')}
                key={updateEscalationForm.key('isNotify')}
                mt={7}
                disabled={!hasUpdateEscalationPolicy}
              />
            </Stack>
            <TextInput
              label="Submitted by"
              readOnly
              classNames={{
                wrapper: styles['record-info-wrapper'],
                input: styles['record-info-input'],
                label: styles['record-info-label'],
              }}
              defaultValue={
                updateEscalationForm.getInputProps('createdBy.username')
                  .defaultValue
              }
            />
            <DatePickerInput
              readOnly
              valueFormat="MM/DD/YYYY"
              placeholder="MM/DD/YYYY"
              label="Submitted Date"
              key={updateEscalationForm.key('createdAt')}
              rightSection={
                <Icons.Calendar className={styles['calender-icon']} />
              }
              highlightToday={true}
              {...updateEscalationForm.getInputProps('createdAt')}
              classNames={{
                input: styles['datePicker-input'],
                label: styles['datePicker-label'],
              }}
            />
            <TextInput
              label="Last Edited By"
              readOnly
              value={
                updateEscalationForm.getValues()['updatedBy']?.username ?? ''
              }
              classNames={{
                wrapper: styles['record-info-wrapper'],
                input: styles['record-info-input'],
                label: styles['record-info-label'],
              }}
            />
            <DatePickerInput
              valueFormat="MM/DD/YYYY"
              placeholder="MM/DD/YYYY"
              label="Last Edited Date"
              readOnly
              key={updateEscalationForm.key('updatedAt')}
              rightSection={
                <Icons.Calendar className={styles['calender-icon']} />
              }
              highlightToday={true}
              {...updateEscalationForm.getInputProps('updatedAt')}
              classNames={{
                input: styles['datePicker-input'],
                label: styles['datePicker-label'],
              }}
            />
          </SimpleGrid>
          <EscalationCaseForm
            updateEscalationForm={updateEscalationForm}
            isSubmittedForm={isSubmittedForm}
            program={updateEscalationForm.getValues()['program']!}
            hasEditPolicy={!hasUpdateEscalationPolicy}
          />
          {chatId && <EscalationNotes chatId={chatId} />}
          <UploadEscalationAttachments
            updateEscalationForm={updateEscalationForm}
            disabled={!hasUpdateEscalationPolicy}
          />
          <Flex justify="space-between" mt={50} mb={10}>
            {!isSubmittedForm ? (
              <Button
                variant="default"
                className={styles['button-cancel']}
                disabled={
                  !hasUpdateEscalationPolicy ||
                  updateEscalationMutationStatus.isLoading
                }
                loading={updateEscalationMutationStatus.isLoading}
                type="submit"
                id={UpdateFormSubmitter.SAVE_DRAFT}
              >
                Save & Complete Later
              </Button>
            ) : (
              <Button
                variant="default"
                className={styles['button-cancel']}
                disabled={updateEscalationMutationStatus.isLoading}
                loading={updateEscalationMutationStatus.isLoading}
                onClick={() => modals.closeAll()}
              >
                Cancel
              </Button>
            )}
            {!isSubmittedForm ? (
              <Button
                rightSection={<Icons.ArrowRight />}
                className={styles['button-save']}
                type="submit"
                disabled={
                  !hasUpdateEscalationPolicy ||
                  updateEscalationMutationStatus.isLoading
                }
                loading={updateEscalationMutationStatus.isLoading}
                id={UpdateFormSubmitter.SUBMIT}
              >
                Submit Form
              </Button>
            ) : (
              <Button
                rightSection={<Icons.ArrowRight />}
                className={styles['button-save']}
                type="submit"
                disabled={
                  !hasUpdateEscalationPolicy ||
                  updateEscalationMutationStatus.isLoading
                }
                loading={updateEscalationMutationStatus.isLoading}
                id={UpdateFormSubmitter.SAVE}
              >
                Save
              </Button>
            )}
          </Flex>
        </form>
      ) : (
        <EscalationModalSkeleton />
      )}
    </Paper>
  )
}

export default withErrorBoundaryAndPolicyCheck(
  UpdateEscalationModal,
  POLICIES.isGetEscalationsEnabled,
)
